import { Trans, useLingui } from '@lingui/react/macro';
import { ChampionshipMediaTab } from 'modules/championship/domain/enums/championship-media-tab';
import { useChampionshipMediaSettingsQuery } from 'modules/championship/store/championship-media-api';
import ChampionshipMediaResults from 'modules/championship/ui/components/form-parts/media/championship-media-results';
import ChampionshipMediaTemplates from 'modules/championship/ui/components/form-parts/media/championship-media-templates';
import { useChampionshipMediaJobs } from 'modules/championship/ui/hooks/use-championship-media-jobs';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useMemo } from 'react';
import { Link, Navigate, Route, Routes, useMatch, useNavigate } from 'react-router-dom';
import { useFeature } from 'shared/hooks/use-feature';
import UDButton from 'ud-ui/components/button';
import { TabMenu } from 'ud-ui/components/tab-menu';
import UDText from 'ud-ui/components/text';

interface ChampionshipMediaPageProps {
  championshipId: number;
  /**
   * Организация, которой принадлежит соревнование (`championship.tournamentId` бэкенда).
   * Паки берутся по ней, а не по активной организации в шапке: супер-админ открывает
   * турниры чужих организаций, а пак другой организации бэкенд привязать не даст (IMGM-1489).
   */
  organizerId: number;
}

const TAB_ORDER = [ChampionshipMediaTab.IMAGES, ChampionshipMediaTab.TEMPLATES];

/**
 * Вкладка «Медиа» соревнования: подвкладки «Картинки» и «Шаблоны», у каждой свой адрес.
 *
 * «Картинки» вынесены наверх отдельной подвкладкой со счётчиком черновиков, а не блоком под
 * списком типов: внизу длинной страницы их не находили (IMGM-1488). Счётчик и ссылка
 * «Посмотреть» в тосте генерации — то, что не даёт подвкладке спрятать результат.
 */
const ChampionshipMediaPage = ({ championshipId, organizerId }: ChampionshipMediaPageProps) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { isEnabled, isFetching: isFeatureFetching } = useFeature('MEDIA_MANAGER');
  const { data: settings, isFetching } = useChampionshipMediaSettingsQuery(championshipId, { skip: !isEnabled });
  const { data: jobs } = useChampionshipMediaJobs(championshipId, isEnabled ? organizerId : 0);
  const isTemplatesRoute = Boolean(useMatch(Pages.CHAMPIONSHIPS.MEDIA.TEMPLATES));

  const draftsCount = useMemo(() => jobs?.items.filter((job) => job.status === 'READY').length ?? 0, [jobs]);

  const tabs = useMemo(
    () => [
      { name: draftsCount > 0 ? t`Картинки · ${draftsCount}` : t`Картинки`, active: true },
      { name: t`Шаблоны`, active: true },
    ],
    [draftsCount, t],
  );

  const handleChangeTab = useCallback(
    (index: number) => navigate(Pages.CHAMPIONSHIPS.MEDIA.builders.tab(championshipId, TAB_ORDER[index]), { replace: true }),
    [navigate, championshipId],
  );

  if (isFeatureFetching) {
    return null;
  }

  if (!isEnabled) {
    return (
      <div className="w-full px-6" data-testid="championship-media-upsell">
        <UDText type="title-3" className="mb-3">
          <Trans>Медиа-менеджер недоступен в вашей подписке</Trans>
        </UDText>
        <UDText type="subhead" className="text-inkSecondary mb-6">
          <Trans>Чтобы привязать пак шаблонов и публиковать картинки автоматически, подключите тариф с медиа-менеджером.</Trans>
        </UDText>
        <Link to={Pages.SETTINGS.builders.subscription('MEDIA_MANAGER')}>
          <UDButton variant="primary" type="button">
            <Trans>Выбрать тариф</Trans>
          </UDButton>
        </Link>
      </div>
    );
  }

  // Пока пака нет, смотреть нечего — первым делом его надо привязать на «Шаблонах».
  const defaultTab = settings && !settings.templateGroup ? ChampionshipMediaTab.TEMPLATES : ChampionshipMediaTab.IMAGES;

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col pb-6" data-testid="championship-media-page">
      <div className="mb-5">
        <TabMenu tabs={tabs} activeTab={isTemplatesRoute ? 1 : 0} onChange={handleChangeTab} />
      </div>
      {/* `flex-1` по всей цепочке: без высоты `UDEntityList` не центрирует пустое состояние «Картинок». */}
      <div className="flex min-h-0 w-full flex-1 flex-col px-6">
        <Routes>
          <Route
            path={ChampionshipMediaTab.IMAGES}
            element={<ChampionshipMediaResults championshipId={championshipId} organizerId={organizerId} />}
          />
          <Route
            path={ChampionshipMediaTab.TEMPLATES}
            element={
              <ChampionshipMediaTemplates
                championshipId={championshipId}
                organizerId={organizerId}
                settings={settings}
                isFetching={isFetching}
              />
            }
          />
          <Route
            path="*"
            element={
              isFetching && !settings ? (
                <div className="skeleton-block h-24 w-full" data-testid="championship-media-skeleton" />
              ) : (
                <Navigate to={Pages.CHAMPIONSHIPS.MEDIA.builders.tab(championshipId, defaultTab)} replace />
              )
            }
          />
        </Routes>
      </div>
    </div>
  );
};

export default ChampionshipMediaPage;
